import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useAuth } from '../../context';
import { login } from '../../api/auth';
import { Form, FormField, CheckBox, SubmitButton } from '../../components/forms';
import { ActivityIndicator } from '../../components/ui';
import { images } from '../../config/colors';
import tocLogoFallback from '../../assets/ToC_Logo.png';
import tocBackgroundFallback from '../../assets/tocBackground.jpg';
import styles from './WelcomePage.module.css';

const tocBackground = images.background || tocBackgroundFallback;
const tocLogo = images.logo || tocLogoFallback;

const validationSchema = Yup.object().shape({
  email: Yup.string().required().email().label('Email'),
  password: Yup.string().required().min(4).label('Password'),
});

interface LoginFormValues {
  email: string;
  password: string;
  keep_alive: boolean;
}

export function WelcomePage() {
  const navigate = useNavigate();
  const { email: savedEmail, setEmail, setLoginKey, loginKey } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loginKey) {
      navigate('/', { replace: true });
    }
  }, [loginKey, navigate]);

  const handleSubmit = async (values: LoginFormValues) => {
    setEmail(values.email);
    setLoading(true);
    setError(null);

    const result = await login(values.email, values.password, values.keep_alive);
    setLoading(false);

    if (!result.ok) {
      setError(result.problem || 'Unexpected error');
      return;
    }

    if (result.data?.error) {
      setError(result.data.error.message || 'Login failed');
      return;
    }

    if (result.data?.login_key) {
      setLoginKey(result.data.login_key);
    }
  };

  return (
    <div
      className={styles.background}
      style={{ backgroundImage: `url(${tocBackground})` }}
    >
      <ActivityIndicator visible={loading} />
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={tocLogo} alt="Taste of Charlotte" className={styles.logo} />
        </div>

        <div className={styles.formContainer}>
          <p className={styles.returningUserNotice}>
            Returning users: your existing Taste of Charlotte account still works! Just sign in with the same email and password.
          </p>
          <Form<LoginFormValues>
            initialValues={{
              email: savedEmail || '',
              password: '',
              keep_alive: false,
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <FormField
              name="email"
              icon="✉"
              placeholder="Email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
            />
            <FormField
              name="password"
              icon="🔒"
              placeholder="Password"
              type="password"
              autoComplete="current-password"
            />
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.checkboxContainer}>
              <CheckBox name="keep_alive" label="Stay Logged In?" />
            </div>
            <SubmitButton title="Guest Login" theme="secondary" />
          </Form>

          <div className={styles.buttonRow}>
            <button
              className={styles.smallButton}
              onClick={() => navigate('/register')}
            >
              Register
            </button>
            <button
              className={styles.smallButton}
              onClick={() => navigate('/forgot-password')}
            >
              Help
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomePage;
